import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { validateEmail, validatePassword } from '../../utils/validation';
import { API_ENDPOINTS } from '../../config/api';
import { authUtils } from '../../utils/auth';

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const validation = validatePassword(value);
    setPasswordErrors(validation.errors);
  };

  // Helper function to check if form is complete and valid
  const isFormValid = () => {
    return (
      name.trim() !== '' &&
      validateEmail(email) &&
      validatePassword(password).isValid &&
      acceptedTerms
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError('Please fix the password errors below');
      return;
    }

    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.REGISTER, {
        name,
        email,
        password,
        recaptchaToken,
      });

      if (response.status === 200 || response.status === 201) {
        authUtils.setEmail(email);
        navigate('/verify-email');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-jobesta shadow-jobesta-card">
        <div>
          <h1 className="text-center text-3xl mb-2 tracking-tight">
            <span className="font-black text-jobesta-blue">Job</span>
            <span className="font-extralight text-jobesta-blue">esta</span>
          </h1>
          <h2 className="text-center text-xl font-semibold text-jobesta-gray-90">
            Create your account
          </h2>
        </div>
        <form className="mt-8 space-y-6 ph-no-capture" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="name" className="sr-only">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-t-md focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-b-md focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
              />
            </div>
          </div>
          
          {passwordErrors.length > 0 && (
            <ul className="mt-2 text-sm text-jobesta-red">
              {passwordErrors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          )}

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="focus:ring-jobesta-lightblue h-4 w-4 text-jobesta-blue border-jobesta-gray-40 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className="text-jobesta-gray-90">
                I agree to the{' '}
                <a
                  href="https://jobesta.com/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-jobesta-blue hover:text-jobesta-darkblue hover:underline"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="https://jobesta.com/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-jobesta-blue hover:text-jobesta-darkblue hover:underline"
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>

          {error && (
            <div className="rounded-jobesta bg-jobesta-red bg-opacity-10 p-4 border border-jobesta-red">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-jobesta-red">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            size="invisible"
          />

          <div className="flex items-center justify-center">
            <Link
              to="/login"
              className="text-sm text-jobesta-blue hover:text-jobesta-darkblue hover:underline transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-jobesta text-white bg-jobesta-blue hover:bg-jobesta-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;